import { roles, rolesPath } from '../../constants/roles';
import { logoutHelper } from '../../utils/logoutHelper';
import { getLoggedInUserId } from '../../utils/storageUtils';
import { apiRequest } from '../../utils/apiRequest';

/**
 * POST /users/sign-in
 *
 * <p>Le back retourne directement {@code JwtAuthenticationResponse} :
 * {@code { accessToken, refreshToken, accessExpiry }} — aucun wrap
 * {success, message, data}. Le saga consommateur doit lire ces champs
 * à la racine puis enchaîner avec {@link #getMe} pour récupérer le profil.
 *
 * <p>{@code silent: true} : le toast global d'{@code apiRequest} est
 * désactivé pour cette route. Un 401 y signifie « identifiants
 * incorrects » (et non « session expirée »), or {@code notifyApiError}
 * avale tous les 401 — l'utilisateur ne voyait donc rien sur un mot de
 * passe erroné. C'est le saga {@code loginUser} qui affiche le message,
 * en un seul endroit et sans doublon.
 *
 * @param {{username: string, password: string}} payload  Identifiant
 *   {@code username} (= userName ou email côté back) + mot de passe.
 * @returns {Promise<{ok: boolean, status: number, data?: any, message?: string}>}
 */
export const loginUser = async (payload) => {
  return apiRequest.post('/users/sign-in', payload, {
    auth: false,
    silent: true
  });
};

/**
 * GET /users/me
 *
 * <p>Endpoint protégé. Retourne le {@code UserResponse} polymorphe
 * (discriminant {@code role}). À appeler immédiatement après login pour
 * peupler {@code userdata} dans le localStorage.
 */
export const getMe = async () => {
  return apiRequest.get('/users/me');
};

/**
 * @deprecated Le listing massif {@code GET /users} est interdit au runtime
 * (cf. Phase 2.H — sweep getUsers). Aucun appel HTTP n'est émis : la fonction
 * retourne immédiatement un envelope 410 avec {@code data: []} pour permettre
 * aux composants legacy (Users, PersonnelMedecin, Assureur, TabOne, modale
 * Patient) de monter sans crash. À supprimer en Phase 4 une fois les
 * endpoints paginés par rôle ({@code /users/staff}, {@code /users/insurers})
 * branchés côté back + front.
 */
export const getUsers = async () => {
  console.warn(
    '[getUsers] Endpoint /users (listing massif) supprimé au runtime — ' +
    'voir Phase 2.H. Retour : envelope 410 vide.'
  );
  return {
    ok: false,
    status: 410,
    data: [],
    message: 'Endpoint /users (listing massif) supprimé — utiliser endpoints paginés par rôle.',
    error: 'Gone'
  };
};

/**
 * GET /users/{id}
 *
 * <p>Retourne un {@code UserResponse} polymorphe (discriminant {@code role}).
 * Utilisé notamment par les sagas {@code createUser} / {@code updateUser}
 * pour récupérer l'objet complet après un POST/PUT qui ne renvoie qu'un
 * {@code Long} (id) côté back.
 */
export const getUserById = async (id) => {
  return apiRequest.get(`/users/${id}`);
};

export const getPatientsPaginated = async (search = '', page = 0, size = 20) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });
  if (search && search.trim()) {
    params.append('search', search.trim());
  }
  return apiRequest.get(`/users/patients?${params.toString()}`);
};

/**
 * GET /users/by-role/{roleName}?page=&size=&search=
 *
 * <p>Liste paginée d'utilisateurs filtrés par rôle (Phase 2.H — remplaçant
 * du listing massif {@code GET /users}). Le shape de la réponse est
 * homogène avec {@code getPatientsPaginated} :
 * {@code { content, totalElements, totalPages, currentPage, pageSize, hasNext, hasPrevious }}.
 *
 * @param {string} roleName  Valeur de l'enum back {@code RoleName}
 *                           (ex : {@code 'ROLE_DOCTOR'}, {@code 'ROLE_INSURER'},
 *                           {@code 'ROLE_PATIENT'}, {@code 'ROLE_SECRETARY'},
 *                           {@code 'ROLE_ADMIN'}).
 * @param {string} [search]  Filtre textuel optionnel (nom/prénom/userName/
 *                           raison sociale/téléphone). Trimmé côté front,
 *                           ignoré si vide.
 * @param {number} [page=0]  Index 0-based de la page demandée.
 * @param {number} [size=20] Taille de page.
 */
export const getUsersByRole = async (roleName, search = '', page = 0, size = 20) => {
  if (!roleName) {
    return {
      ok: false,
      status: 400,
      data: null,
      message: 'getUsersByRole : roleName est requis.'
    };
  }
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });
  if (search && search.trim()) {
    params.append('search', search.trim());
  }
  return apiRequest.get(`/users/by-role/${encodeURIComponent(roleName)}?${params.toString()}`);
};

/**
 * GET /users/staff?page=&size=&search=
 *
 * <p>Liste paginée du <em>personnel interne</em> (staff = tous rôles SAUF
 * {@code ROLE_PATIENT} et {@code ROLE_INSURER}). Alimente la page
 * d'administration {@code Users} qui consolide médecins, secrétaires et
 * admins. Phase 2.H — remplace le filtrage côté front sur le listing massif
 * {@code GET /users} (supprimé).
 *
 * <p>Réponse homogène avec {@code getPatientsPaginated} :
 * {@code { content, totalElements, totalPages, currentPage, pageSize, hasNext, hasPrevious }}.
 *
 * @param {string} [search]  Filtre textuel optionnel (firstName/lastName/
 *                           userName/téléphone). Trimmé côté front.
 * @param {number} [page=0]  Index 0-based de la page demandée.
 * @param {number} [size=20] Taille de page.
 */
export const getStaffPaginated = async (search = '', page = 0, size = 20) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });
  if (search && search.trim()) {
    params.append('search', search.trim());
  }
  return apiRequest.get(`/users/staff?${params.toString()}`);
};

/**
 * @deprecated L'endpoint backend {@code /users/get-filter} a été supprimé
 * (cf. tâche #14). On garde l'export pour compatibilité d'import existante,
 * mais l'appel renvoie une erreur applicative claire au lieu d'un 404 muet.
 */
export const getUsersByFilter = async (_payload) => {
  console.warn(
    '[getUsersByFilter] Endpoint /users/get-filter supprimé — utiliser getPatientsPaginated.'
  );
  return {
    ok: false,
    status: 410,
    message: 'Endpoint /users/get-filter supprimé — utiliser getPatientsPaginated.',
    error: 'Gone'
  };
};

/**
 * Charge le profil de l'utilisateur connecté en interrogeant
 * {@code GET /users/{id}} avec l'id stocké dans {@code userdata}.
 *
 * <p>Note : pour la majorité des cas, préférer {@link #getMe} qui ne
 * dépend pas du localStorage. Ce service est conservé pour les écrans
 * legacy qui dispatch {@code GET_USER_PROFILE_REQUEST} sans avoir
 * l'id sous la main.
 */
export const getUserProfile = async () => {
  const userId = getLoggedInUserId();
  if (!userId) {
    logoutHelper();
    return { ok: false, status: 401, message: 'Session introuvable. Veuillez vous reconnecter.' };
  }

  const result = await apiRequest.get(`/users/${userId}`);
  if (!result.ok && result.status === 401) {
    logoutHelper();
  }
  return result;
};

export const registerInsurer = async (payload) => {
  return apiRequest.post('/users/insurer', payload);
};

/**
 * POST /users/{staff|patient|insurer}
 *
 * <p>Le payload est un {@code StaffRequest} / {@code PatientRequest} /
 * {@code InsurerRequest} côté back ; tous portent le champ {@code roleName}
 * (RoleName enum). Attention : {@code roleName} est le champ <em>request</em>
 * (legacy, conservé pour compat back). En réponse, le back retourne juste
 * un {@code Long} (id) — pas l'objet utilisateur. Pour récupérer
 * l'objet complet, le saga doit chaîner avec {@link #getUserById}.
 *
 * @param {object} payload  Doit contenir {@code roleName} pour router vers
 *   l'endpoint correct.
 * @returns {Promise<{ok: boolean, status: number, data?: number, message?: string}>}
 *   {@code data} est l'id (Long) du nouvel utilisateur si {@code ok}.
 */
export const createUser = async (payload) => {
  const segment = rolesPath[payload.roleName];
  if (!segment) {
    return {
      ok: false,
      status: 400,
      message: `Rôle inconnu : ${payload.roleName}`
    };
  }

  // L'InsurerRequest exige un champ `name` (raison sociale) ; on retombe sur
  // le `userName` si l'UI ne l'a pas saisi explicitement.
  const data = payload.roleName === roles.ROLE_INSURER
    ? { ...payload, name: payload?.name || payload?.userName }
    : payload;

  return apiRequest.post(`/users/${segment}`, data);
};

/**
 * PUT /users/{id}
 *
 * <p>Comme {@link #createUser}, le back retourne juste un {@code Long} (id).
 * Le saga doit chaîner avec {@link #getUserById} pour rafraîchir l'objet.
 */
export const updateUser = async (payload) => {
  return apiRequest.put(`/users/${payload.id}`, payload);
};

/**
 * DELETE /users/{id}?replacementDoctorId=
 *
 * <p>Le back retourne 204 No Content ; {@code apiRequest} renvoie alors
 * {@code { ok: true, status: 204, data: null }}. La suppression d'un compte
 * du personnel est en réalité une <em>désactivation</em> : le compte
 * disparaît de {@code GET /users/staff} et de
 * {@code GET /users/by-role/ROLE_DOCTOR} mais son historique est conservé.
 *
 * <p>Cas particulier du médecin : {@code replacementDoctorId} est
 * <strong>obligatoire</strong>. Le back réaffecte au médecin désigné les
 * patients suivis et les entrées de file d'attente du médecin supprimé,
 * puis désactive le compte. Sans ce paramètre il répond 400 avec le message
 * « Indiquez le médecin à qui affecter les patients de ce médecin. » ; il
 * répond également 400 si le remplaçant est le médecin supprimé lui-même
 * ou n'est pas un médecin, et 409 si un patient ou un assureur reste
 * rattaché au compte.
 *
 * @param {number|string} id  Identifiant du compte à supprimer.
 * @param {{replacementDoctorId?: number|string}} [options]
 *   {@code replacementDoctorId} : médecin destinataire du transfert.
 *   Omis pour les rôles non médecins.
 * @returns {Promise<{ok: boolean, status: number, data?: any, message?: string}>}
 */
export const deleteUser = async (id, options = {}) => {
  const { replacementDoctorId } = options;
  const hasReplacementDoctor =
    replacementDoctorId !== undefined &&
    replacementDoctorId !== null &&
    replacementDoctorId !== '';

  if (!hasReplacementDoctor) {
    return apiRequest.del(`/users/${id}`);
  }

  const params = new URLSearchParams({
    replacementDoctorId: replacementDoctorId.toString()
  });
  return apiRequest.del(`/users/${id}?${params.toString()}`);
};

export const getPatientCard = async (id) => {
  return apiRequest.get(`/users/patient-card-pdf/${id}`);
};

/** @deprecated logoutHelper expose la déconnexion réelle. */
export const logout = () => {};
